import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Box } from '@mui/material'
import { BudgetA4NoteDialog } from '../../components/BudgetA4Dialog'
import { CommonNoteDialog } from '../../components/CommonNoteDialog'
import { DeliveryNoteDialog } from '../../components/DeliveryNoteDialog'
import { LegalNoteDialog } from '../../components/LegalNoteDialog'
import { useCoin } from '../../context/coin_context'
import {
    CreditNoteInfo,
    DeliveryInfo,
    ParcelInfo,
    PaymentInSale,
    PreSaleInfo,
    SaleInfo,
} from '../../context/sale_context'
import {
    findCreditNote,
    findCreditNoteById,
    getPreSaleById,
    getSaleById,
    listDelivery,
    listParcels,
    listSalePayments,
} from '../../services/sale'
import { useLoading } from '../../context/loading_context'
import { useTranslation } from 'react-i18next'
import { PaymentNoteDialog } from '../../components/PaymentNoteDialog'
import { TransferStockInfo } from '../Products/TransferStock'
import { listTransferStock } from '../../services/product'
import { CreditNoteDialog } from '../../components/CreditNoteDialog'
import { TransferStockNoteDialog } from '../../components/TransferStockNoteDialog'
import { ParcelNoteDialog } from '../../components/ParcelNoteDialog'
import { useBranch } from '../../context/branch_context'
import { SystemBranchEnum, useConfigs } from '../../context/configs_context'

export function RenderNote() {
    const { t } = useTranslation()
    const [searchParams] = useSearchParams()
    const { coins } = useCoin()
    const { configs, initializedLogo } = useConfigs()
    const { initializedLogos: initializedLogosBranch } = useBranch()
    const [sales, setSales] = useState<SaleInfo[]>([])
    const [budgets, setBudgets] = useState<PreSaleInfo[]>([])
    const [deliveries, setDeliveries] = useState<DeliveryInfo[]>([])
    const [payments, setPayments] = useState<PaymentInSale[]>([])
    const [creditNotes, setCreditNotes] = useState<CreditNoteInfo[]>([])
    const [transferStocks, setTransferStocks] = useState<TransferStockInfo[]>(
        []
    )
    const [parcelToRender, setParcelToRender] = useState<ParcelInfo | null>(
        null
    )
    const [parcelsForInvoice, setParcelsForInvoice] = useState<ParcelInfo[]>([])
    const loading = useLoading()

    useEffect(() => {
        if (!coins.length || !configs.id || !initializedLogo) return
        if (configs.systemBranch === SystemBranchEnum.Branch && !initializedLogosBranch) return

        const saleId = searchParams.get('saleId')
        const preSaleId = searchParams.get('preSaleId')
        const creditNoteId = searchParams.get('creditNoteId')
        const parcelId = searchParams.get('parcelId')
        const attachPayments = searchParams.get('attachPayments')
        const attachCreditNotes = searchParams.get('attachCreditNotes')
        const attachTransferStocks = searchParams.get('attachTransferStocks')
        if (saleId)
            handleLoadSaleData(
                Number(saleId),
                attachPayments === 'true',
                attachCreditNotes === 'true',
                attachTransferStocks === 'true',
                parcelId ? Number(parcelId) : null
            )
        if (preSaleId) handleLoadPreSaleData(Number(preSaleId))
        if (creditNoteId) handleLoadCreditNoteData(Number(creditNoteId))
    }, [coins, configs, initializedLogo, initializedLogosBranch])

    const handleLoadSaleData = async (
        saleId: number,
        attachPayments: boolean,
        attachCreditNotes: boolean,
        attachTransferStocks: boolean,
        parcelId?: number | null
    ) => {
        loading.show()
        try {
            const sale = await getSaleById(saleId, coins)

            if (parcelId) {
                const parcels = await listParcels({ id_venda: saleId })
                setParcelsForInvoice(parcels)
                setParcelToRender(
                    parcels.find((parcel) => parcel.id === parcelId) ?? null
                )
            }

            if (sale.preSaleId) {
                const preSale = await getPreSaleById(sale.preSaleId, coins)
                setBudgets([preSale])
            }
            const deliveries = await listDelivery(
                {
                    id_venda: sale.id,
                },
                coins
            )
            if (attachPayments) {
                const payments = await listSalePayments(
                    {
                        id_venda: [saleId],
                    },
                    coins
                )
                setPayments(payments.data)
            }
            if (attachCreditNotes) {
                const creditNotes = await findCreditNote(
                    {
                        id_venda: saleId,
                        loadFullRelations: true,
                    },
                    coins
                )
                setCreditNotes(creditNotes.data)
            }
            if (attachTransferStocks) {
                const transferStocks = await listTransferStock({
                    id_venda: [saleId],
                    loadFullRelations: true,
                })
                setTransferStocks(transferStocks.data)
            }

            setDeliveries(deliveries.data)
            setSales([sale])
        } catch (error) {}
        loading.hide()
    }

    const handleLoadPreSaleData = async (preSaleId: number) => {
        loading.show()
        try {
            const preSale = await getPreSaleById(preSaleId, coins)
            setBudgets([preSale])
        } catch (error) {}
        loading.hide()
    }

    const handleLoadCreditNoteData = async (creditNoteId: number) => {
        loading.show()
        try {
            const creditNote = await findCreditNoteById(creditNoteId, coins)
            setCreditNotes([creditNote])
        } catch (error) {}
        loading.hide()
    }

    const NoteComponent = sales[0]?.note ? LegalNoteDialog : CommonNoteDialog

    if (loading.isLoading) return <></>

    return (
        <Box
            className="printable"
            style={{ display: 'block' }}
            sx={{
                isolation: 'isolate',
                backgroundColor: '#fff',
                minHeight: '100vh',
                width: '100%',
            }}
        >
            {sales.length > 0 &&
                (parcelToRender ? (
                    <ParcelNoteDialog
                        sale={sales[0]}
                        totalParcels={parcelsForInvoice.length}
                        parcels={[parcelToRender]}
                        handleClose={() => {}}
                        dontPrint
                    />
                ) : (
                    <NoteComponent
                        sales={sales}
                        handleClose={() => {}}
                        dontPrint
                    />
                ))}
            {budgets.length > 0 && (
                <BudgetA4NoteDialog
                    budgets={budgets}
                    handleClose={() => {}}
                    dontPrint
                />
            )}
            {deliveries.length > 0 && (
                <DeliveryNoteDialog
                    deliveries={deliveries}
                    handleClose={() => {}}
                    dontPrint
                />
            )}
            {payments.length > 0 && (
                <PaymentNoteDialog
                    payments={payments}
                    handleClose={() => {}}
                    showLegalData
                    dontPrint
                />
            )}
            {creditNotes.length > 0 && (
                <CreditNoteDialog
                    creditNotes={creditNotes}
                    handleClose={() => {}}
                    dontPrint
                />
            )}
            {transferStocks.length > 0 && (
                <TransferStockNoteDialog
                    transferStocks={transferStocks}
                    handleClose={() => {}}
                    dontPrint
                />
            )}
        </Box>
    )
}
